import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { extendViewService } from 'tuc-webui-base/services/ViewService';
import ConfirmDialog from 'tuc-webui-base/components/ConfirmDialog';
import { FormatBasedEditor } from 'tuc-webui-base/components/editors';
import MessageDialog from 'grb-webui/components/MessageDialog';
import { ActionButtons } from 'grb-webui/components/Actions';
import { StateEditor } from 'grb-webui/editors';
import { HISTORY_JOBS_TYPE } from '../../ComponentTypes';
import { JobDurationEditor, QueuedJobDurationEditor } from '../../components/JobDurationEditor';
import JobActions from './JobActions';
import JobsHeaderButtons from './JobsHeaderButtons';
import JobStatus, { getJobStatusStates } from './JobStatus';
import JobBatch from './JobBatch';
import JobParameters from './JobParameters';
import OptimizationStatus from './OptimizationStatus';
import JobSolveStatus from './JobSolveStatus';
import './JobsContainer.scss';

const replaceWorkerJobsWithParents = (nodes) => {
  const parentNodes = [];
  const filteredNodes = nodes.filter((node) => {
    if (node.parent) {
      parentNodes.push(node.parent);
    }
    return true;
  });
  parentNodes.forEach((node) => {
    if (filteredNodes.indexOf(node) < 0) {
      filteredNodes.push(node);
    }
  });
  return filteredNodes;
};

class Clock {
  constructor() {
    this.now = new Date().getTime();
    this.subscribers = [];
    this.start();
  }

  updateTime() {
    this.time = new Date().getTime();
    this.subscribers.forEach(s => (s(this.time)));
  }

  subscribe(s) {
    this.subscribers.push(s);
    return s;
  }

  unsubscribe(s) {
    const i = this.subscribers.indexOf(s);
    if (i >= 0) {
      this.subscribers.splice(i, 1);
    }
    return null;
  }

  start() {
    this.stop();
    if (!this.timer) {
      this.timer = setInterval(this.updateTime.bind(this), 1000);
    }
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

const numbered = (str, n) => `${n} ${str}${(n > 1) ? 's' : ''}`;

export default (Component, options = {}) => {
  const { jobType, paging, jobService: js } = options;
  const getJobParentId = js.getJobParentId.bind(js);
  const getJobDistributedWorkerCount = js.getJobDistributedWorkerCount.bind(js);
  const getJobAddress = js.getJobAddress.bind(js);
  const getJobBatchId = js.getJobBatchId.bind(js);
  const getJobGroup = js.getJobGroup.bind(js);
  const getJobStatusLabel = js.getJobStatusLabel.bind(js);
  const getJobSolveStatusLabel = js.getJobSolveStatusLabel.bind(js);
  const getJobStatusTime = js.getJobStatusTime.bind(js);
  const isJobWaiting = js.isJobWaiting.bind(js);
  const actions = options.actions ? { ...JobActions(options), ...options.actions } : JobActions(options);
  const { rowButtons = [], useRowButtonsLabel = true } = options;

  const ViewServiceProperties = {
    componentsByName: {
      JobButtons: ActionButtons(actions, rowButtons, { useLabel: useRowButtonsLabel }),
      JobsHeaderButtons: JobsHeaderButtons(options),
      JobBatch,
      JobStatus: JobStatus(options),
      JobParameters,
      JobSolveStatus,
      OptimizationStatus,
      ...(options.componentsByName || {}),
    },
    editors: [
      new JobDurationEditor('jobDuration', options),
      new QueuedJobDurationEditor('jobQueueWait', options),
      new FormatBasedEditor('jobStatus', (state, job) => {
        return getJobStatusLabel(job);
      }, {
        filterValue: (value, fvalue) => {
          return fvalue;
        },
      }),
      new StateEditor('jobStatus', {
        getStateValue: (v, job) => js.getJobStatus(job),
        states: getJobStatusStates(options).filter(x => x),
      }),
      new FormatBasedEditor('queueIndex', (index, job) => {
        return (index === undefined) || !isJobWaiting(job) ? '' : `${index}`;
      }, { sortValue: (value) => (value || 0) }),
      new FormatBasedEditor('solveStatus', getJobSolveStatusLabel),
      new FormatBasedEditor('jobStatusTime', (d) => {
        if (!d) return '';
        const date = new Date(d);
        return [date.toLocaleDateString(), date.toLocaleTimeString()];
      }, {
        value: getJobStatusTime,
        filterValue: (value, fvalue) => (Array.isArray(fvalue) ? fvalue.join(' ') : fvalue),
        sortValue: d => (d === undefined ? 0 : new Date(d).getTime()),
        multiline: true,
        multilineSeparator: ' ',
      }),
      ...(options.editors || []),
    ],
    iconsByName: options.iconsByName || {},
  };

  const tableModel = {
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canCheck: jobType !== HISTORY_JOBS_TYPE, // History jobs cannot be aborted
    parentId: paging ? undefined : getJobParentId,
    getExtraFilterValues: job => [job.id, getJobAddress(job), getJobBatchId(job), getJobGroup(job)],
    reuseSameObjects: true,
    validateChecks: replaceWorkerJobsWithParents,
  };

  //
  // JobsContainer component
  //
  const JobsContainer = (props) => {
    const {
      viewService,
      jobService,
      onSnackbarMessage,
    } = props;
    const [dialogMessage, setDialogMessage] = useState({ open: false });
    const [confirmDialog, setConfirmDialog] = useState({ open: false });
    const [extViewService] = useState(() => extendViewService(viewService, ViewServiceProperties));
    const [clock] = useState(() => new Clock());

    useEffect(() => {
      return () => {
        clock.stop();
      };
    }, []);

    //
    // Abort job
    //
    const handleCloseMessageDialog = useCallback(() => {
      setDialogMessage({ open: false });
    }, []);

    const handleAbortJob = useCallback((ajobs) => {
      const abortJobs = ({ jobsToAbort, nbJobs } = {}) => {
        setConfirmDialog({ open: false });
        if (nbJobs) {
          jobService.abortJobs(jobsToAbort)
          .then(() => {
            onSnackbarMessage({ message: `${numbered('job', nbJobs)} aborted` });
          })
          .catch(e => viewService.onError(e));
        }
      };

      const js = Array.isArray(ajobs) ? ajobs : [ajobs];
      const toAbortIds = js.map(m => m.id);
      let nbAssocWorkers = 0;
      let nbWorkers = 0;
      let nbMainJobs = 0;
      const invalidJobMessages = [];
      const jobsToAbort = js.filter((job) => {
        const parentId = getJobParentId(job);
        // Filter worker machines which master server belongs to the list of machine to terminate
        if (parentId && (toAbortIds.indexOf(parentId) >= 0)) {
          return false;
        }
        const result = jobService.validateAbort(job);
        if ((result !== true) && (result !== null)) {
          invalidJobMessages.push(result);
        }
        // Job can be aborted
        if (!parentId) {
          nbMainJobs += 1;
          nbAssocWorkers += getJobDistributedWorkerCount(job) || 0;
        } else if (toAbortIds.indexOf(parentId) < 0) { // Otherwise taken care of in nbAssocWorkers
          nbWorkers += 1;
        }
        return true;
      });

      if (invalidJobMessages.length === js.length) {
        if (invalidJobMessages.length) {
          setDialogMessage({
            title: 'Abort Job Warning',
            message: invalidJobMessages[0],
            open: true,
          });
        }
      } else {
        let message;
        const nbJobs = nbAssocWorkers + nbWorkers + nbMainJobs;
        if (invalidJobMessages.length) {
          message = 'Some jobs cannot be aborted, do you want to continue?';
        } else if (nbMainJobs) {
          message = !nbAssocWorkers
            ? `Are you sure you want to abort ${numbered('job', nbJobs)}?`
            : `Aborting a job will abort all its distributed worker jobs.\nAre you sure you want to abort ${numbered('job', nbJobs)}?`;
        } else {
          message = `Are you sure you want to abort ${numbered('worker', nbWorkers)}?`;
        }
        setConfirmDialog({
          open: true,
          title: 'Confirm Abort',
          message,
          buttons: [
            { title: 'Cancel', value: false, id: 'grb-cancel-job-abort' },
            { title: 'Abort Job(s)', value: { jobsToAbort, nbJobs }, id: 'grb-confirm-job-abort' },
          ],
          onClose: abortJobs,
        });
      }
    }, [jobService]);

    // Messages
    const handleSetDialogMessage = useCallback((params) => {
      setDialogMessage(params);
    }, []);

    const handleSetConfirmDialog = useCallback((params) => {
      setConfirmDialog(params);
    }, []);

    return (
      <React.Fragment>
        <Component
          {...props}
          viewService={extViewService}
          tableModel={tableModel} /* For tree table model */
          onAbort={handleAbortJob}
          clock={clock}
          setDialogMessage={handleSetDialogMessage}
          setConfirmDialog={handleSetConfirmDialog}
        />
        {dialogMessage.open && (<MessageDialog {...dialogMessage} onClose={handleCloseMessageDialog} />)}
        {confirmDialog.open && (<ConfirmDialog {...confirmDialog} maxWidth="md" />)}
      </React.Fragment>
    );
  };

  JobsContainer.propTypes = {
    viewService: PropTypes.shape({
      onError: PropTypes.func,
      getComponent: PropTypes.func,
    }).isRequired,
    route: PropTypes.shape({}).isRequired,
    location: PropTypes.shape({
      state: PropTypes.shape({
        selectedBatchId: PropTypes.string,
      }),
    }).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func,
    }).isRequired,
    query: PropTypes.shape({
      batchId: PropTypes.string,
    }),
    jobService: PropTypes.shape({
      validateAbort: PropTypes.func,
      abortJobs: PropTypes.func,
      getJobLogURL: PropTypes.func,
    }).isRequired,
    component: PropTypes.shape({}),
    components: PropTypes.arrayOf(PropTypes.shape({})),
    arrayFilter: PropTypes.func,
    onFilterChange: PropTypes.func,
    filter: PropTypes.shape({
      length: PropTypes.string,
    }),
    filterEditor: PropTypes.shape({}),
    onSnackbarMessage: PropTypes.func.isRequired,
    onUpdateJobs: PropTypes.func,
  };

  JobsContainer.defaultProps = {
    filter: undefined,
    arrayFilter: undefined,
    onFilterChange: undefined,
    filterEditor: undefined,
    onUpdateJobs: undefined,
    component: undefined,
    components: undefined,
    query: {},
  };

  return JobsContainer;
};
